import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Play, ArrowLeft, Clock, Globe, Filter, Search,
  Wifi, WifiOff, Volume2, CheckCircle, Heart, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const formatLabels = {
  'chair_yoga': 'Chair Yoga',
  'dance_cardio': 'Dance Cardio',
  'tai_chi': 'Tai Chi',
  'strength_bodyweight': 'Bodyweight Strength',
  'circuit_training': 'Circuit Training',
  'guided_walk_audio': 'Guided Walk',
  'functional_fitness': 'Functional Fitness'
};

export default function MovementLibrary() {
  const { workoutId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [workouts, setWorkouts] = useState([]);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    track: 'all',
    format: 'all',
    maxDuration: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompletion, setShowCompletion] = useState(false);
  const [legacyMoment, setLegacyMoment] = useState('');
  const [completing, setCompleting] = useState(false);

  const fetchWorkouts = useCallback(async () => {
    try {
      let url = `${API}/api/fitness/library`;
      const params = new URLSearchParams();
      if (filters.track !== 'all') params.append('track', filters.track);
      if (filters.format !== 'all') params.append('format', filters.format);
      if (filters.maxDuration !== 'all') params.append('max_duration', filters.maxDuration);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setWorkouts(data.workouts);
      }
    } catch (err) {
      console.error('Error fetching workouts:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchWorkout = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/api/fitness/library/${id}`);
      if (res.ok) {
        setSelectedWorkout(await res.json());
      }
    } catch (err) {
      console.error('Error fetching workout:', err);
    }
  }, []);

  useEffect(() => {
    fetchWorkouts();
  }, [fetchWorkouts]);

  useEffect(() => {
    if (workoutId) {
      fetchWorkout(workoutId);
    } else {
      setSelectedWorkout(null);
    }
  }, [workoutId, fetchWorkout]);

  const handleComplete = async () => {
    if (!user) {
      toast.error('Please log in to track workouts');
      return;
    }

    setCompleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/fitness/workout/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          workout_id: selectedWorkout.id,
          duration_minutes: selectedWorkout.duration_minutes,
          legacy_moment_response: legacyMoment || null
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.badges_earned?.length > 0) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}!`);
        }
        setShowCompletion(false);
        setLegacyMoment('');
        navigate('/fitness/passport');
      }
    } catch (err) {
      toast.error('Failed to log workout');
    } finally {
      setCompleting(false);
    }
  };

  const filteredWorkouts = workouts.filter(w => {
    if (!searchQuery) return true;
    return w.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           w.cultural_origin.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  // Workout Detail View
  if (selectedWorkout) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-rose-50/30" data-testid="workout-detail">
        <div className="bg-gradient-to-br from-rose-600 to-pink-700 text-white py-12">
          <div className="container mx-auto px-4">
            <Link to="/fitness/library" className="inline-flex items-center text-white/80 hover:text-white mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Library
            </Link>
            
            <div className="flex items-start gap-4">
              <div className="p-4 bg-white/20 rounded-xl">
                <Play className="h-10 w-10" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-white/20 text-white border-0">{formatLabels[selectedWorkout.format] || selectedWorkout.format}</Badge>
                  <Badge className="bg-white/20 text-white border-0">
                    <Clock className="h-3 w-3 mr-1" /> {selectedWorkout.duration_minutes} min
                  </Badge>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{selectedWorkout.title}</h1>
                <p className="text-rose-100 flex items-center">
                  <Globe className="h-4 w-4 mr-2" /> {selectedWorkout.cultural_origin}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <Card className="mb-6">
            <CardContent className="py-6">
              <p className="text-stone-700 text-lg mb-6">{selectedWorkout.description}</p>
              
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedWorkout.offline_available && (
                  <Badge variant="outline" className="text-green-700 border-green-300">
                    <WifiOff className="h-3 w-3 mr-1" /> Offline Available
                  </Badge>
                )}
                {selectedWorkout.audio_only_available && (
                  <Badge variant="outline" className="text-blue-700 border-blue-300">
                    <Volume2 className="h-3 w-3 mr-1" /> Audio Only
                  </Badge>
                )}
                {selectedWorkout.modifications_included && (
                  <Badge variant="outline" className="text-purple-700 border-purple-300">
                    <CheckCircle className="h-3 w-3 mr-1" /> Modifications
                  </Badge>
                )}
              </div>

              <div className="flex gap-4">
                <Button 
                  className="flex-1 bg-rose-600 hover:bg-rose-700 h-14 text-lg"
                  onClick={() => setShowCompletion(true)}
                >
                  <Play className="h-5 w-5 mr-2" /> Start Workout
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Legacy Moment Preview */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-6">
              <div className="flex items-start gap-3">
                <Heart className="h-6 w-6 text-amber-600 mt-1" />
                <div>
                  <p className="font-medium text-amber-800 mb-2">Legacy Moment</p>
                  <p className="text-amber-700 italic">"{selectedWorkout.legacy_moment_prompt}"</p>
                  <p className="text-sm text-amber-600 mt-2">You'll be prompted to reflect after completing.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Completion Dialog */}
        <Dialog open={showCompletion} onOpenChange={setShowCompletion}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Complete Workout
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 rounded-lg">
                <p className="font-medium text-amber-800 mb-2">Legacy Moment</p>
                <p className="text-amber-700 italic mb-4">"{selectedWorkout.legacy_moment_prompt}"</p>
                <Textarea
                  value={legacyMoment}
                  onChange={(e) => setLegacyMoment(e.target.value)}
                  placeholder="Share your reflection... (optional)"
                  className="min-h-[100px]"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowCompletion(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleComplete}
                  disabled={completing || !user}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {completing ? 'Saving...' : 'Complete'}
                </Button>
              </div>
              {!user && (
                <p className="text-sm text-center text-stone-500">
                  <Link to="/login" className="text-rose-600 hover:underline">Log in</Link> to track
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Library List View
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-rose-50/30" data-testid="movement-library">
      <div className="bg-gradient-to-br from-rose-600 to-pink-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/fitness" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyFit
          </Link>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Movement Library</h1>
          <p className="text-rose-100">{workouts.length} workouts from around the world</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search workouts..."
                  className="pl-10"
                />
              </div>
              <Select value={filters.track} onValueChange={(v) => setFilters(f => ({ ...f, track: v }))}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Track" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tracks</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="legacy_advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.format} onValueChange={(v) => setFilters(f => ({ ...f, format: v }))}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Formats</SelectItem>
                  <SelectItem value="chair_yoga">Chair Yoga</SelectItem>
                  <SelectItem value="dance_cardio">Dance Cardio</SelectItem>
                  <SelectItem value="strength_bodyweight">Strength</SelectItem>
                  <SelectItem value="tai_chi">Tai Chi</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.maxDuration} onValueChange={(v) => setFilters(f => ({ ...f, maxDuration: v }))}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Length</SelectItem>
                  <SelectItem value="15">15 min or less</SelectItem>
                  <SelectItem value="30">30 min or less</SelectItem>
                  <SelectItem value="45">45 min or less</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Workouts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkouts.map((workout, idx) => (
            <motion.div
              key={workout.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Link to={`/fitness/library/${workout.id}`}>
                <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs capitalize">
                        {workout.track.replace('_', ' ')}
                      </Badge>
                      <span className="text-sm text-stone-500 flex items-center">
                        <Clock className="h-3 w-3 mr-1" /> {workout.duration_minutes}m
                      </span>
                    </div>
                    <CardTitle className="text-lg">{workout.title}</CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <Globe className="h-3 w-3" /> {workout.cultural_origin}
                      <span className="text-stone-300">|</span>
                      {formatLabels[workout.format] || workout.format}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-1 mb-3">
                      {workout.offline_available && <WifiOff className="h-4 w-4 text-stone-400" />}
                      {workout.audio_only_available && <Volume2 className="h-4 w-4 text-stone-400" />}
                    </div>
                    <Button className="w-full bg-rose-600 hover:bg-rose-700">
                      <Play className="h-4 w-4 mr-2" /> Start
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>

        {filteredWorkouts.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Play className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-700 mb-2">No workouts found</h3>
              <p className="text-stone-500">Try adjusting your filters.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
